<script setup lang="ts">
import type { HTMLAttributes, Ref } from 'vue'
import type { IconResolveEvent } from './injectionKeys';
import type { IconName } from './vocabulary'
import { cn } from '@elveris/ui/lib/utils'
import { Icon as IconifyIcon } from '@iconify/vue'
import { computed, inject, isRef, ref, watchEffect } from 'vue'
import { pickResolvingFamily } from './compatibility'
import { extendedRegistry } from './extend'
import { DEFAULT_FAMILY, LAZY_LOADERS, registerLocalIconLoaders } from './families'
import {
  ICON_FALLBACK_FAMILY_INJECTION_KEY,
  ICON_FAMILY_INJECTION_KEY,
  ICON_LOCALE_INJECTION_KEY,
  ICON_OVERRIDES_INJECTION_KEY,
  ICON_SECONDARY_FAMILY_INJECTION_KEY,
  ICON_STYLE_INJECTION_KEY,
  ICON_TELEMETRY_INJECTION_KEY,
  ICON_WEIGHT_INJECTION_KEY

} from './injectionKeys'
import { resolveLocalisedLabel } from './locale'
import { resolveFqnForFamily } from './resolve'
import { DIRECTIONAL_ICONS, ICON_LABELS } from './vocabulary'

interface Props {
  /**
   * Semantic vocabulary name — resolves through the active theme
   * family + fallback chain + per-tenant overrides. Accepts any
   * built-in `IconName` (autocomplete) or a consumer-extended concept
   * registered via `extendVocabulary()` (no autocomplete; consumer
   * should declare a local type alias). Mutually exclusive with
   * `icon` (literal escape hatch).
   */
  name?: IconName | (string & {})
  /**
   * Literal `family:name` (e.g. `lucide:check`, `ph:rocket-bold`).
   * Bypasses theme resolution entirely. Use for one-off icons
   * outside vocabulary, for any of Iconify's 200,000+ icons.
   */
  icon?: string
  class?: HTMLAttributes['class']
  /**
   * `inherit` sizes the icon to the surrounding text (1em), the way a bare
   * `@iconify/vue` icon renders — the migration path for icons that relied on it.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'inherit' | number
  variant?: 'default' | 'brand' | 'success' | 'error' | 'warning' | 'info' | 'muted'
  ariaLabel?: string
  /**
   * Pause SMIL (`<animate>`, `<animateTransform>`) + CSS animations
   * baked into the icon body. Defaults to `'auto'` which honours
   * `prefers-reduced-motion: reduce` — so motion-sensitive users
   * automatically get a still frame without consumer wiring. Set to
   * `true` to force-pause regardless of preference (e.g. inside a
   * non-animated table row), `false` to override the media query.
   */
  paused?: boolean | 'auto'
  /**
   * Horizontal flip control for direction-bound concepts (chevrons,
   * arrows, login/logout glyphs — see `DIRECTIONAL_ICONS`).
   *   - `'auto'` (default): flip when the rendered SVG inherits
   *     `dir="rtl"` AND the concept is in `DIRECTIONAL_ICONS`. A
   *     `chevron-right` rendered inside Arabic copy reads as "next"
   *     after flipping, matching reader expectation.
   *   - `true`: always flip.
   *   - `false`: never flip — for graphics that already encode
   *     direction independently (e.g. a "shopping cart" with a
   *     directional handle that should stay LTR even in RTL UI).
   */
  flip?: boolean | 'auto'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'default',
  paused: 'auto',
  flip: 'auto',
})

const familyProvider = inject(ICON_FAMILY_INJECTION_KEY, DEFAULT_FAMILY)
const secondaryFamilyProvider = inject(ICON_SECONDARY_FAMILY_INJECTION_KEY, undefined as string | undefined)
const fallbackFamilyProvider = inject(ICON_FALLBACK_FAMILY_INJECTION_KEY, undefined as string | undefined)
const localeProvider = inject(ICON_LOCALE_INJECTION_KEY, 'en' as const)
const overridesProvider = inject(ICON_OVERRIDES_INJECTION_KEY, {} as Record<string, string>)
const weightProvider = inject(ICON_WEIGHT_INJECTION_KEY, undefined as string | undefined)
const styleProvider = inject(ICON_STYLE_INJECTION_KEY, undefined as string | undefined)
const telemetryProvider = inject(ICON_TELEMETRY_INJECTION_KEY, undefined)

function unwrap<T>(value: T | Ref<T>): T {
  return isRef(value) ? value.value : value
}

// Trigger lazy-load for any family in the resolution chain that ships
// its JSON catalogue out-of-band. Iconify caches the registration
// once it completes.
//
// SSR safety: `LAZY_LOADERS` use `addCollection` from @iconify/vue,
// which mutates a browser-side cache. Running them server-side is
// pointless work (the server render emits the FQN string; iconify's
// runtime cache doesn't survive the SSR/CSR boundary anyway) AND
// risky — `addCollection` reaches for window-bound globals in some
// codepaths. The `isBrowser` guard makes the watch a no-op during
// SSR; the hydrating client then re-runs the watch and warms the
// cache before the icon is painted.
// Bundled collections answer Iconify's cache misses locally — see
// registerLocalIconLoaders(). Must precede the first <IconifyIcon> render.
registerLocalIconLoaders()

const isBrowser = typeof window !== 'undefined'
const lazyLoadAttempts = new Set<string>()
function ensureLazyLoaded(family: string | undefined): void {
  if (!isBrowser) return
  if (!family || lazyLoadAttempts.has(family)) return
  const loader = LAZY_LOADERS[family]
  if (!loader) return
  lazyLoadAttempts.add(family)
  loader().catch((err: unknown) => {
    if (typeof console !== 'undefined' && (import.meta.env?.DEV ?? false)) {
      console.warn(`[@elveris/ui] icon lazy-load failed for ${family}:`, err)
    }
  })
}

watchEffect(() => {
  ensureLazyLoaded(unwrap(familyProvider) as string)
  ensureLazyLoaded(unwrap(secondaryFamilyProvider))
  ensureLazyLoaded(unwrap(fallbackFamilyProvider))
})

/**
 * Internal trace structure — computed once, consumed by the rendered
 * FQN AND the telemetry watch effect. Keeps both in lock-step so a
 * monitor never sees an event for a different FQN than what iconify
 * rendered.
 *
 * SSR hydration safety: every input to this computed is deterministic
 * across server + client (props, the immutable `coverage.json` import,
 * the static `extendedRegistry` ref state, the resolved provider
 * values). `pickResolvingFamily` + `resolveFqnForFamily` are pure.
 * The FQN string the server emits to the HTML therefore matches what
 * the client computes on first render — no hydration mismatch unless
 * the consumer mutates `extendedRegistry` between SSR and CSR (in
 * which case they should pass `persist: 'inline'` via the extend
 * adapter so the registry hydrates identically).
 */
interface InternalResolution {
  fqn: string
  event: IconResolveEvent | null
}

const resolution = computed<InternalResolution>(() => {
  // Tier 0 — explicit `icon=` overrides every theme tier. Decorative
  // path: no telemetry since no semantic concept is involved.
  if (props.icon) return { fqn: props.icon, event: null }
  if (!props.name) return { fqn: '', event: null }

  // Tier 1 — per-tenant override (brand-specific concept swap).
  const overrides = unwrap(overridesProvider)
  const override = overrides[props.name]
  if (override) {
    const family = override.split(':')[0] ?? 'unknown'
    return {
      fqn: override,
      event: { concept: props.name, fqn: override, family, tier: 'override' },
    }
  }

  // Tier 2-4 — primary → secondary → fallback chain. `pickResolvingFamily`
  // consults the build-time coverage map + the extended registry to
  // choose the FIRST tier that actually has the concept.
  const primary = (unwrap(familyProvider) as string) || DEFAULT_FAMILY
  const secondary = unwrap(secondaryFamilyProvider)
  const fallback = unwrap(fallbackFamilyProvider)
  const winningFamily = pickResolvingFamily(props.name, primary, secondary, fallback)

  const variantCtx = {
    weight: unwrap(weightProvider),
    style: unwrap(styleProvider),
  }
  const fqn = resolveFqnForFamily(props.name, winningFamily, variantCtx)

  // Classify which tier won for telemetry. `extended` takes precedence
  // when the concept is consumer-registered, since the registry result
  // can sit on any tier visually but is conceptually a separate kind.
  const tier: IconResolveEvent['tier']
    = props.name in extendedRegistry.value
      ? 'extended'
      : winningFamily === primary
        ? 'primary'
        : winningFamily === secondary
          ? 'secondary'
          : winningFamily === fallback
            ? 'fallback'
            : 'hardcoded-lucide'
  return {
    fqn,
    event: { concept: props.name, fqn, family: winningFamily, tier },
  }
})

const resolvedIcon = computed(() => resolution.value.fqn)

// Telemetry sink. Fires once per resolution change. We avoid a
// computed side-effect (Vue's reactivity model frowns on those) and
// route the emission through a watcher instead. Decorative renders
// (event == null) don't invoke the callback.
watchEffect(() => {
  const event = resolution.value.event
  if (!event) return
  const sink = unwrap(telemetryProvider)
  if (typeof sink === 'function') {
    try {
      sink(event)
    }
    catch (err) {
      if (typeof console !== 'undefined' && (import.meta.env?.DEV ?? false)) {
        console.warn('[@elveris/ui] icon telemetry sink threw:', err)
      }
    }
  }
})

const resolvedAriaLabel = computed(() => {
  if (props.ariaLabel) return props.ariaLabel
  if (!props.name) return undefined
  const locale = unwrap(localeProvider)
  // Consumer-extended labels first; covers custom concepts not in
  // the built-in `ICON_LABELS` table. `resolveLocalisedLabel` walks
  // the full BCP-47 chain (e.g. `pt-BR` → `pt` → `en`) so partial
  // localisations don't starve.
  const ext = extendedRegistry.value[props.name]
  if (ext?.labels) {
    return resolveLocalisedLabel(ext.labels, locale)
  }
  const labels = ICON_LABELS[props.name as IconName]
  return resolveLocalisedLabel(labels, locale)
})

const iconClasses = computed(() => {
  const sizeMap: Record<string, string> = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-10 w-10',
    'inherit': 'h-[1em] w-[1em]',
  }

  const variantMap: Record<string, string> = {
    default: 'text-current',
    brand: 'text-theme-brand',
    success: 'text-theme-ok',
    error: 'text-theme-err',
    warning: 'text-theme-warn',
    info: 'text-theme-info',
    muted: 'text-theme-neutral-6',
  }

  return cn(
    'flex-shrink-0 transition-colors duration-200',
    typeof props.size === 'string' ? sizeMap[props.size] : '',
    variantMap[props.variant],
    props.class,
  )
})

const iconSize = computed(() => (typeof props.size === 'number' ? props.size : undefined))

/**
 * Pause state. `'auto'` resolves to `true` only when the user has
 * `prefers-reduced-motion: reduce` set — guaranteeing motion-sensitive
 * users a still frame without consumer wiring. Explicit `true`/`false`
 * always wins.
 *
 * SSR safety: `window.matchMedia` is browser-only; on the server we
 * default to `false` (no pause) so the SSR HTML matches an unmuted
 * client preference. The watch below re-evaluates on hydration if the
 * client actually has reduced-motion enabled.
 */
const prefersReducedMotion = ref(false)
if (isBrowser && typeof window.matchMedia === 'function') {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  prefersReducedMotion.value = mq.matches
  // Listen for live preference flips (macOS Accessibility toggle, etc.).
  mq.addEventListener?.('change', (e) => { prefersReducedMotion.value = e.matches })
}

const isPaused = computed(() => {
  if (props.paused === true) return true
  if (props.paused === false) return false
  return prefersReducedMotion.value
})

/**
 * Inline style that suspends both CSS keyframe animation and SMIL
 * (`<animate>`, `<animateTransform>`) playback for the icon subtree.
 * `animation-play-state: paused` handles CSS; SMIL doesn't have a
 * dedicated CSS knob, so we set `pointer-events: none` (no impact on
 * paint) and use `animation-name: none` as a defensive secondary
 * cancellation. Browsers that ignore one still honour the other.
 */

/**
 * Text direction sampled from `<html dir="…">` at setup time. Default
 * 'ltr' for SSR determinism; the browser-side setup re-reads on
 * hydration. Mid-session i18n flips (rare — usually route-bound)
 * don't propagate to already-mounted icons; consumers that need live
 * updates should drive the `flip` prop reactively.
 */
const currentDir = ref<'ltr' | 'rtl'>('ltr')
if (isBrowser) {
  currentDir.value = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr'
}

const shouldFlip = computed(() => {
  if (props.flip === true) return true
  if (props.flip === false) return false
  // 'auto' — flip only when both conditions hold.
  if (!props.name) return false
  return currentDir.value === 'rtl' && DIRECTIONAL_ICONS.has(props.name as never)
})

/**
 * Style block combining pause + flip transforms. Both must coexist on
 * the same `style=` attribute since `<IconifyIcon>` only accepts a
 * single binding.
 */
const composedStyle = computed(() => {
  const base: Record<string, string> = {}
  if (isPaused.value) {
    base.animationPlayState = 'paused'
    base.animation = 'none'
  }
  if (shouldFlip.value) {
    base.transform = 'scaleX(-1)'
  }
  return Object.keys(base).length > 0 ? base : undefined
})
</script>

<template>
  <IconifyIcon
    :icon="resolvedIcon"
    :class="iconClasses"
    :width="iconSize"
    :height="iconSize"
    :aria-label="resolvedAriaLabel"
    :role="resolvedAriaLabel ? 'img' : undefined"
    :style="composedStyle"
    v-bind="$attrs"
  />
</template>
